<template>
  <div>
    <div class="card fixed-height">
      <div class="card-header d-flex justify-content-between">
        <h4 class="p-0 m-0">
          <small>
            <i class="fa fa-circle text-cr-red-black"></i>
          </small>
          <span v-if="showAddItemMode">
            {{ $t('phrase.add_a_question') }}
          </span>
          <span v-else-if="editResearchItemId">
            {{ $t('phrase.edit_item') }}
          </span>
          <span v-else>
            {{ $t('phrase.questions_in_research') }}
          </span>
        </h4>
        <div v-if="!editResearchItemId">
          <a
            v-if="!showAddItemMode"
            class="btn btn-secondary mr-2"
            target="_blank"
            :href="`/res/${research.uuid}/?test=true`"
          >
            <i class="fa fa-pencil mr-2"></i>
            {{ $t('phrase.test_research') }}
          </a>
          <v-button
            id="research-edit-add-item-button"
            :style-type="showAddItemMode ? 'secondary' : 'primary'"
            :text="showAddItemMode ? '' : 'phrase.add_a_question'"
            :icon="showAddItemMode ? 'times' : 'plus'"
            @click="showAddItemMode = !showAddItemMode"
          />
        </div>
      </div>
      <div
        class="card card-body fixed-height card-items overflow-auto"
        :class="{'bg-light': researchItemListMode, 'bg-white': !researchItemListMode}"
        ref="researchItemsContainer"
      >
        <transition name="fade-nl">
          <v-item-add-container
            v-if="showAddItemMode"
            :research-type-slug="research.research_type.slug"
            :organisation="research.organisation"
            @created="addItemToResearch"
            @selected="addItemToResearch"
          />
        </transition>
        <transition name="fade-nl">
          <v-item-update-container
            v-if="editResearchItemId"
            :research-item-id="editResearchItemId"
            :organisation-slug="research.organisation.slug"
            @updated="refreshItems()"
            @cancel="editResearchItemId = null"
          />
        </transition>
        <div v-if="researchItemListMode">
          <div
            v-for="researchItem in lockedResearchItems"
            :key="researchItem.id"
            class="mb-2"
          >
            <v-research-item
              :research-item="researchItem"
              :index="researchItem.itemIndex"
              :is-locked="true"
            />
          </div>

          <div
            class="mb-2"
            v-if="addedResearchItems.length > 0"
          >
            <v-draggable
              style="min-height: 120px"
              v-model="addedResearchItems"
              group="additional-items"
              @update="itemsUpdatedHandler"
            >
              <div
                v-for="researchItem in addedResearchItems"
                :key="researchItem.uuid"
                class="mb-2"
              >
                <v-research-item
                  :researchItem="researchItem"
                  :edit-mode="true"
                  :index="researchItem.itemIndex"
                  @delete-item="deleteItem"
                  @edit-item="editItem"
                  @duplicate-item="duplicateItem"
                />
              </div>
            </v-draggable>
          </div>
          <div class="mt-4 w-100 d-flex flex-column align-items-center">
            <div
              class="text-center text-muted mb-2"
              v-if="addedResearchItems.length === 0"
            >
              {{ $t('sentence.no_questions_in_research') }}
            </div>
            <v-button
              id="research-edit-add-item-button"
              style-type="primary"
              text="phrase.add_a_question"
              icon="plus"
              @click="showAddItemMode = true"
            />
          </div>
        </div>
      </div>
      <div
        class="overlay d-flex flex-column justify-content-center align-items-center"
        v-if="loading"
      >
        <div class="text-center" v-if="fetchResearchItemsError">
          <h1>
            <i class="fa fa-exclamation-triangle text-danger"></i>
          </h1>
          <h5>{{ $t('sentence.something_went_wrong') }}</h5>
        </div>
        <div class="text-center" v-else>
          <div class="mb-2">
            <v-loader/>
          </div>
          <h4>{{ loaderText }}</h4>
        </div>
      </div>
      <div
        v-if="researchItemListMode"
        class="card-footer d-flex justify-content-end align-items-center"
      >
        <h3
          class="p-0 m-0 mr-3 d-none d-lg-inline"
          v-if="price"
        >
          <small class="text-total-price text-muted">{{ $t('word.total_price') }}: </small>
          &euro;
          <v-animated-number
            :value="price.toFixed(0)"
            :formatValue="(value) => (value.toFixed(0))"
            :duration="500"
          />,00
        </h3>

        <v-button
          id="research-edit-next-step-button"
          text="phrase.save_and_next"
          icon="arrow-right"
          style-type="primary"
          @click="$emit('next-step')"
        />
      </div>
    </div>
    <v-item-duplicate-modal
      v-if="duplicateResearchItemId"
      :research-item-id="duplicateResearchItemId"
      @updated="refreshItems()"
      @hide-modal="duplicateResearchItemId = null"
    />
  </div>
</template>

<script>
import AnimatedNumber from 'animated-number-vue';
import Button from '@/common/components/Button.vue';
import draggable from 'vuedraggable';
import ResearchItem from '@/organisation/components/research/ResearchItem.vue';
import ItemAddContainer from '@/organisation/components/ItemAddContainer.vue';
import ItemDuplicateModal from '@/organisation/components/ItemDuplicateModal.vue';
import Loader from '@/common/components/Loader.vue';
import {
  createResearchItem,
  fetchResearchItems,
  updateResearchItemsOrder,
  deleteResearchItem,
} from '@/organisation/api';
import { mapActions, mapGetters } from 'vuex';
import ItemUpdateContainer from '@/organisation/components/ItemUpdateContainer.vue';

export default {
  name: 'ResearchStructureEdit',
  components: {
    'v-animated-number': AnimatedNumber,
    'v-button': Button,
    'v-draggable': draggable,
    'v-item-add-container': ItemAddContainer,
    'v-item-duplicate-modal': ItemDuplicateModal,
    'v-item-update-container': ItemUpdateContainer,
    'v-loader': Loader,
    'v-research-item': ResearchItem,
  },
  data() {
    return {
      addedResearchItems: [],
      duplicateResearchItemId: null,
      editResearchItemId: null,
      fetchResearchItemsError: false,
      loaderText: '',
      loading: false,
      lockedResearchItems: [],
      showAddItemMode: false,
      editResearchMode: false,
    };
  },
  props: {
    research: {
      type: Object,
      required: true,
    },
    organisation: {
      type: Object,
      required: true,
    },
    researchType: {
      type: Object,
      required: true,
    },
    price: {
      type: Number,
      required: false,
    },
  },
  methods: {
    ...mapActions(['refreshUser']),
    async addItemToResearch(itemId) {
      const response = await createResearchItem({
        item_id: itemId,
        research_uuid: this.research.uuid,
      });
      if (response.status === 201) {
        this.showAddItemMode = false;
        await this.refreshItems();

        const container = this.$refs.researchItemsContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      } else {
        this.errors = response.data;
      }
    },
    async fetchResearchItems() {
      const response = await fetchResearchItems(this.research.uuid);
      if (response.status === 200) {
        this.addedResearchItems = response.data
          .filter((researchItem) => researchItem.is_locked === false);
      } else {
        this.fetchResearchItemsError = true;
      }
    },
    async deleteItem(researchItem) {
      this.loading = true;
      const response = await deleteResearchItem(researchItem.id);
      if (response.status !== 204) {
        alert('Something went wrong. Please try again.');
      }
      await this.refreshResearchItems(false);
      this.loading = false;
    },
    editItem(researchItem) {
      this.editResearchItemId = researchItem.id;
    },
    duplicateItem(researchItem) {
      this.duplicateResearchItemId = researchItem.id;
    },
    async itemsUpdatedHandler() {
      this.loading = true;
      const newOrder = this.lockedResearchItems
        .map((researchItem) => researchItem.id)
        .concat(this.addedResearchItems.map((researchItem) => researchItem.id));
      const response = await updateResearchItemsOrder(newOrder);
      if (response.status !== 200) {
        alert('Something went wrong. Please try again.');
        return;
      }
      await this.refreshResearchItems(false);
      this.loading = false;
    },
    async refreshItems() {
      this.loading = true;
      this.showAddItemMode = false;
      this.editResearchItemId = null;
      await this.fetchResearchItems();
      this.loading = false;
    },
    async refreshResearchItems(scrollDown) {
      const response = await fetchResearchItems(this.research.uuid);
      if (response.status !== 200) {
        this.fetchResearchItemsError = true;
        return;
      }
      let itemIndex = 0;
      this.lockedResearchItems = response.data
        .filter((researchItem) => researchItem.is_locked === true)
        .map((researchItem) => {
          if (researchItem.item.render_type !== 'media') {
            itemIndex += 1;
          }
          return {
            itemIndex,
            ...researchItem,
          };
        });

      this.addedResearchItems = response.data
        .filter((researchItem) => researchItem.is_locked === false)
        .map((researchItem) => {
          if (researchItem.item.render_type !== 'media') {
            itemIndex += 1;
          }
          return {
            itemIndex,
            ...researchItem,
          };
        });
      this.loading = false;

      if (scrollDown) {
        const container = this.$refs.researchItemsContainer;
        if (container) {
          setTimeout(() => {
            container.scrollTop = container.scrollHeight;
          }, 10);
        }
      }
    },
  },
  watch: {
    addedResearchItems(researchItems) {
      this.$emit('items-updated', researchItems.length);
    },
  },
  computed: {
    ...mapGetters(['org', 'user']),
    researchItemListMode() {
      return !this.showAddItemMode && !this.editResearchItemId;
    },
  },
  created() {
    this.loading = true;
    this.loaderText = this.$t('phrase.questions_loaded');
    this.refreshResearchItems(true);
  },
};
</script>

<style scoped>
.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 1000;
}

.fixed-height {
  height: calc(100vh - 120px);
}

.card-items {
  transition: background-color 0.3s;
  scroll-behavior: smooth;
}
</style>
